import React, { useState, useEffect } from 'react';
import { pick, prop } from 'ramda';
import useApp from 'reducers/hooks/useApp';
import { isBlank } from 'utils/helperMethods';
import PropTypes from 'prop-types';
import { Box, Typography } from '@material-ui/core';

import { useTranslation } from 'react-i18next';
import { calculateBidValues } from 'utils/bidHelper';
import BidRequestPresenter from 'presenters/BidRequestPresenter';
import { initialValues } from 'forms/bidForm';
import useBid from 'reducers/hooks/useBid';

import UserPresenter from 'presenters/UserPresenter';
import BidPresenter, { INITIAL_BID_FORM_VALUES } from 'presenters/BidPresenter';
import BidForm from './components/BidForm';
import AutoCalculation from './components/AutoCalculation';

import useStyles from './useStyles';

const BidPage = props => {
  const { bidRequest, bid, offeringsLoading, offeringOptions, onCreateBid, onReloadBidRequest } = props;
  const { bidsFormInputValues, setBidFormInputValues, changeBidFormInputValue } = useBid();

  const { currentUser } = useApp();
  const bidDisclosure = UserPresenter.bidDisclosure(currentUser);

  const classes = useStyles();
  const { t } = useTranslation();
  const currentBidValues = prop(bidRequest.id, bidsFormInputValues);

  useEffect(() => {
    if (isBlank(currentBidValues)) {
      setBidFormInputValues({ bidRequestId: bidRequest.id, values: { ...initialValues, notes: bidDisclosure } });
    }
  }, []);

  const buildBidFormState = () => {
    const bidRequestForm = {
      homePrice: BidRequestPresenter.getPriceTarget(bidRequest),
      period: BidRequestPresenter.preferredTerm(bidRequest),
      creditScore: BidRequestPresenter.creditScore(bidRequest),
      loanAmount: BidRequestPresenter.getLoanAmmount(bidRequest),
      ltv: BidRequestPresenter.getLtv(bidRequest),
      bidRequestId: bidRequest.id,
    };

    if (isBlank(currentBidValues)) return { ...INITIAL_BID_FORM_VALUES, ...bidRequestForm, notes: bidDisclosure };
    return calculateBidValues({ ...INITIAL_BID_FORM_VALUES, ...bidRequestForm, ...currentBidValues });
  };

  const [bidFormValues, setBidFormValues] = useState(buildBidFormState());

  const handleMakeBid = async () => {
    const params = pick(
      [
        'apr',
        'bidRequestId',
        'estimatedTaxes',
        'estimatedTotalMonthlyPayment',
        'fiveYearsPrincipalPaid',
        'fiveYearsTotal',
        'interestRate',
        'notes',
        'offeringId',
        'upfrontCharges',
      ],
      bidFormValues,
    );
    await onCreateBid(params);
    onReloadBidRequest();
    setBidFormInputValues({ bidRequestId: bidRequest.id, values: { ...initialValues, notes: bidDisclosure } });
  };

  const makeBidOutsideGuidelines = async () => {
    const params = pick(
      [
        'apr',
        'estimatedTaxes',
        'estimatedTotalMonthlyPayment',
        'fiveYearsPrincipalPaid',
        'fiveYearsTotal',
        'interestRate',
        'notes',
        'offeringId',
        'upfrontCharges',
      ],
      INITIAL_BID_FORM_VALUES,
    );
    const { bidRequestId } = bidFormValues;

    await onCreateBid({ ...params, bidRequestId });
    onReloadBidRequest();
    setBidFormInputValues({ bidRequestId: bidRequest.id, values: { ...initialValues, notes: bidDisclosure } });
  };

  const handleBidFormValueChange = (fieldName, value) => {
    const updatedBidFormValues = { ...bidFormValues, [fieldName]: value };
    const calculatedBidFormValues = calculateBidValues(updatedBidFormValues);
    setBidFormValues({ ...bidFormValues, ...calculatedBidFormValues });
    changeBidFormInputValue({ bidRequestId: bidRequest.id, field: fieldName, value });
  };

  return (
    <Box className={classes.container}>
      <Typography variant="h2">{t('components.LoanOptions.BidPage.header')}</Typography>
      <BidForm
        offeringOptions={offeringOptions}
        offeringsLoading={offeringsLoading}
        onMakeBid={handleMakeBid}
        onMakeBidOutsideGuidelines={makeBidOutsideGuidelines}
        onChange={handleBidFormValueChange}
        previousBid={bid}
        currentBidValues={currentBidValues}
        bidDisclosure={bidDisclosure}
      />
      <AutoCalculation bidFormValues={bidFormValues} />
    </Box>
  );
};

BidPage.propTypes = {
  bidRequest: BidRequestPresenter.shape().isRequired,
  bid: BidPresenter.shape(),
  offeringOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  offeringsLoading: PropTypes.bool.isRequired,
  onCreateBid: PropTypes.func.isRequired,
  onReloadBidRequest: PropTypes.func.isRequired,
};

BidPage.defaultProps = {
  bid: {},
};

export default BidPage;



// WEBPACK FOOTER //
// ./src/sharedComponents/LoanOptions/components/BidPage/BidPage.js